'use client';

import React, { useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import Link from 'components/consumer/Link';
import classNames from 'classnames';
import avo from 'analytics/avo';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import useUserCurrentPageType from 'hooks/useUserCurrentPageType';
import { getAdvancedSearchUrlForQueryAndIndex } from 'components/consumer/consumer-search/commons/helpers';

const getLabel = (indexName, query) => {
  const capitalized = query.charAt(0).toUpperCase() + query.slice(1);

  switch (indexName) {
    case ALGOLIA_INDEX_NAME.PRODUCTS:
      return 'Products';
    case ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS:
      return `${capitalized} in Customer Photos`;
    case ALGOLIA_INDEX_NAME.REVIEWS:
      return `${capitalized} in Reviews`;
    case ALGOLIA_INDEX_NAME.STATIC_PAGES:
      return `${capitalized} in Pages`;
    default:
      return '';
  }
};

const CATEGORY_LINKS = [
  {
    indexName: ALGOLIA_INDEX_NAME.PRODUCTS,
    urlIndex: 'products',
  },
  {
    indexName: ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS,
    urlIndex: ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS,
  },
  {
    indexName: ALGOLIA_INDEX_NAME.REVIEWS,
    urlIndex: ALGOLIA_INDEX_NAME.REVIEWS,
  },
  {
    indexName: ALGOLIA_INDEX_NAME.STATIC_PAGES,
    urlIndex: 'static_pages_new',
  },
];

const ResultsHeader = ({ variant = 'desktop', children }) => (
  <div
    className={classNames('mx-0 my-2.5 text-base font-bold', {
      'text-gray': variant !== 'mobile',
      'text-brand block mt-[15px] mb-[7px]': variant === 'mobile',
    })}
  >
    {children}
  </div>
);

/**
 * ResultsByCategory - Next/v7 mobile variant
 */
const ResultsByCategoryNextMobile = ({
  toggleSearchOpen,
  variant = 'mobile',
}) => {
  const { results, scopedResults } = useInstantSearch();
  const query = results?.query || '';
  const currentPage = useUserCurrentPageType();

  const hitsByIndex = useMemo(() => {
    const map = {};
    (scopedResults || []).forEach(({ indexId, results: r }) => {
      if (r) map[indexId] = r.nbHits;
    });
    return map;
  }, [scopedResults]);

  if (!query || !results) {
    return null;
  }

  const linkClassName = classNames(
    'text-sm font-bold mt-1 mb-1 ml-0 no-underline !leading-[1.5rem] active:bg-gray-light8 focus:bg-gray-light8 hover:bg-gray-light8',
    {
      'block mr-2.5 text-[#585858]': variant === 'desktop',
      'text-sm px-2 py-1 rounded-[3px] text-gray font-bold mr-2.5 bg-gray-light9 inline-block':
        variant === 'mobile',
    }
  );

  const visibleLinks = CATEGORY_LINKS.filter(
    ({ indexName }) => hitsByIndex[indexName] > 0
  );

  if (!visibleLinks.length) {
    return null;
  }

  return (
    <>
      <ResultsHeader variant={variant}>View results by:</ResultsHeader>
      <div>
        {visibleLinks.map(({ indexName, urlIndex }) => (
          <Link
            key={indexName}
            to={getAdvancedSearchUrlForQueryAndIndex(query, urlIndex)}
            onClick={async () => {
              await toggleSearchOpen?.();
              avo.productsSearched({
                query,
                location: currentPage,
                searchTarget: avo.SearchTarget.OTHER_RESULTS_LINK,
              });
            }}
            className={linkClassName}
          >
            {getLabel(indexName, query)} ({hitsByIndex[indexName]})
          </Link>
        ))}
      </div>
    </>
  );
};

export default ResultsByCategoryNextMobile;
